'use client';

import { useEffect } from 'react';

import { createSupabaseBrowserClient } from '@/lib/supabase/client';
import { getTenantConfigFromSession } from '@/lib/tenant/session';

import { paths } from '@/constants/paths';

function clearSessionAndRedirect() {
  document.cookie = 'tenant_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  window.location.href = paths.auth.login;
}

async function checkSession() {
  const tenantConfig = getTenantConfigFromSession();
  if (!tenantConfig?.supabase_url || !tenantConfig?.supabase_anon_key) return;

  try {
    const supabase = createSupabaseBrowserClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      // Shared cart pages are public — never force a redirect to login from here
      if (
        window.location.pathname !== paths.auth.login &&
        !window.location.pathname.startsWith('/cart/')
      ) {
        clearSessionAndRedirect();
      }
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at ?? 0;
    // If token expired or within 5 min of expiry, force refresh
    if (expiresAt - now < 300) {
      await supabase.auth.refreshSession();
    }
  } catch {
    // silent — middleware will handle redirect if truly unauthenticated
  }
}

/**
 * Salon use-case: tabs left open for hours, or auth cookie deleted externally.
 * - On visibilitychange → visible: force a session check and redirect if gone
 * - On 30s interval: catch cases where user never switches tabs
 */
export function useSessionRevival() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      await checkSession();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    const interval = setInterval(checkSession, 30_000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);
}
